/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import {
  Search,
  Terminal,
  CheckCircle2,
  XCircle,
  Play,
  Globe,
  Zap,
  Database,
  ChevronRight,
  AlertTriangle,
  Clock,
  ArrowLeft,
  Lightbulb,
  Target,
  Layers,
} from 'lucide-react';

import { Input } from '../ui/input';

import { useExecutionData, type ProcessedEvent } from './inspector-context';
import { SmartMessage, JsonHighlight } from './json-highlight';

// ─── Props ─────────────────────────────────────────────────────────────────────

interface ConsolePanelProps {
  events?: unknown[]; // ignored – data pulled from context
  startAt?: number; // ignored – data pulled from context
  onJumpToEvent?: ((idx: number) => void) | undefined;
  selectedIdx?: number | null;
}

// ─── Types ─────────────────────────────────────────────────────────────────────

type LogLevel = 'all' | 'log' | 'info' | 'warn' | 'error' | 'debug';
const LOG_LEVELS: LogLevel[] = ['all', 'log', 'info', 'warn', 'error', 'debug'];
type MilestoneKind = 'start' | 'end' | 'error';
type NarrativeItem =
  | { kind: 'milestone'; event: ProcessedEvent; label: string; milestoneKind: MilestoneKind }
  | { kind: 'span'; event: ProcessedEvent; logs: ProcessedEvent[] }
  | { kind: 'log'; event: ProcessedEvent };

// ─── Name cleaning ─────────────────────────────────────────────────────────────
// Strips internal prefixes like _auto_api.X.Y_fnName → "X.Y (RPC)"

export function cleanSpanName(raw: string | undefined, cap: string): string {
  if (!raw) return cap;
  let name = raw;
  // Strip leading _auto_api. / __auto_api. patterns
  name = name.replace(/^_+auto_api\./, '');
  // If it ends with _<word> that matches the last segment, strip it (deduplication)
  // e.g. "executions.list_getProjectIds" → "executions.list"
  const dotParts = name.split('.');
  const last = dotParts[dotParts.length - 1] ?? '';
  const underIdx = last.lastIndexOf('_');
  if (underIdx > 0) {
    name = dotParts.slice(0, -1).concat(last.slice(0, underIdx)).join('.');
  }
  // Collapse leftover leading dots or underscores
  name = name.replace(/^[._]+/, '');
  return name || raw;
}

// ─── Narrative builder ─────────────────────────────────────────────────────────

function buildNarrative(events: ProcessedEvent[], consoleLogs: ProcessedEvent[]): NarrativeItem[] {
  const items: NarrativeItem[] = [];
  const usedLogKeys = new Set<string>();
  function logKey(log: ProcessedEvent): string {
    return String(log.id) + '@' + log.at;
  }

  const spanWindows = events
    .filter((e): boolean => (e as { phase?: string }).phase === 'span')
    .map((e) => ({ event: e, start: e.at, end: e.at + (e.durationMs ?? 0) }));

  const nestedLogsPerSpan = new Map<string, ProcessedEvent[]>();
  for (const log of consoleLogs) {
    let bestSpan: ProcessedEvent | null = null;
    let bestWindow = Infinity;
    for (const w of spanWindows) {
      const win = w.end - w.start;
      if (log.at >= w.start && log.at <= w.end && win < bestWindow) {
        bestWindow = win;
        bestSpan = w.event;
      }
    }
    if (bestSpan) {
      const spanKey = String(bestSpan.id) + '@' + bestSpan.at;
      if (!nestedLogsPerSpan.has(spanKey)) nestedLogsPerSpan.set(spanKey, []);
      nestedLogsPerSpan.get(spanKey)!.push(log);
      usedLogKeys.add(logKey(log));
    }
  }

  for (const ev of events) {
    const cap = ev.capability;
    const phase = (ev as { phase?: string }).phase;
    const action = ev.action;
    if (cap === 'vm' && action === 'boot') {
      items.push({
        kind: 'milestone',
        event: ev,
        label: 'Execution started',
        milestoneKind: 'start',
      });
      continue;
    }
    if (cap === 'vm' && action === 'return') {
      items.push({
        kind: 'milestone',
        event: ev,
        label: 'Execution completed',
        milestoneKind: 'end',
      });
      continue;
    }
    if (phase === 'error' && cap === 'vm') {
      items.push({
        kind: 'milestone',
        event: ev,
        label: 'Execution failed',
        milestoneKind: 'error',
      });
      continue;
    }
    if (phase === 'span') {
      const spanKey = String(ev.id) + '@' + ev.at;
      const logs = (nestedLogsPerSpan.get(spanKey) ?? [])
        .slice()
        .sort((a, b): number => a.at - b.at);
      items.push({ kind: 'span', event: ev, logs });
      continue;
    }
    if (cap === 'console' && !usedLogKeys.has(logKey(ev))) {
      items.push({ kind: 'log', event: ev });
    }
  }
  return items;
}

// ─── Execution analysis engine ─────────────────────────────────────────────────

interface SequentialGroup {
  spans: ProcessedEvent[];
  totalMs: number; // sequential sum
  parallelMs: number; // max (if parallelized)
  savingsMs: number; // totalMs - parallelMs
  savingsPct: number;
  // Dependency analysis
  likelyIndependent: boolean; // heuristic: no payload dependency detected
  confidence: number; // 0-100 — how confident we are about parallelization safety
  confidenceReason: string; // human-readable explanation of confidence
  dependencyHint?: string | undefined; // e.g. "projectId from getUser response" when dependency detected
}

interface ExecutionAnalysis {
  topSpan: ProcessedEvent | null; // single biggest span by durationMs
  topSpanPct: number; // % of execution time
  executionTotalMs: number;
  sequentialGroups: SequentialGroup[]; // groups of sequential IO spans
  optimizedMs: number; // estimated total if all sequential groups parallelized
  totalSavingsMs: number; // executionTotalMs - optimizedMs
  speedupFactor: number; // executionTotalMs / optimizedMs
}

/** Two spans are considered sequential (non-overlapping) if one ends before the other starts (with 5ms tolerance). */
function spansAreSequential(a: ProcessedEvent, b: ProcessedEvent): boolean {
  const aEnd = a.at + (a.durationMs ?? 0);
  const bEnd = b.at + (b.durationMs ?? 0);
  // No meaningful overlap
  return aEnd <= b.at + 5 || bEnd <= a.at + 5;
}

/**
 * Heuristic: check if span B's request payload contains any value from span A's response.
 * If it does, B likely depends on A and they cannot safely be parallelized.
 * Returns { dependent: boolean, confidence: number (0-100), reason: string }.
 */
function detectPayloadDependency(
  a: ProcessedEvent,
  b: ProcessedEvent,
): { dependent: boolean; confidence: number; reason: string; matchedKey?: string | undefined } {
  const aRes = (a as { responsePayload?: unknown }).responsePayload;
  const bReq = (b as { requestPayload?: unknown }).requestPayload;

  // If either payload is missing/encrypted, we can't tell — medium confidence
  if (aRes == null || bReq == null) {
    return { dependent: false, confidence: 60, reason: 'No payload data to analyze' };
  }

  const aResStr = typeof aRes === 'string' ? aRes : JSON.stringify(aRes);
  const bReqStr = typeof bReq === 'string' ? bReq : JSON.stringify(bReq);

  // Protected payloads — can't inspect
  if (aResStr.includes('"__protected":true') || bReqStr.includes('"__protected":true')) {
    return {
      dependent: false,
      confidence: 50,
      reason: 'Payload is encrypted — cannot detect dependency',
    };
  }

  // Extract meaningful scalar values from A's response (ids, tokens, keys)
  // Avoid matching tiny/common values like {}, [], true, false, 0, 1
  // Also skip infrastructure strings (RPC method names, framework identifiers)
  const aResVals: string[] = [];
  const infraPatterns = [
    /^__auto_api\./, // fluxRun internal RPC method prefix
    /^_auto_api\./, // alternate prefix
    /^(call|response|request|error|span|phase)$/i, // generic payload keys
  ];
  function isInfraString(v: string): boolean {
    return infraPatterns.some((p): boolean => p.test(v));
  }
  try {
    const parsed = typeof aRes === 'string' ? JSON.parse(aRes) : aRes;
    function extractStrings(v: unknown, depth = 0): void {
      if (depth > 4) return;
      if (typeof v === 'string' && v.length >= 8 && !isInfraString(v)) aResVals.push(v);
      else if (typeof v === 'number' && String(v).length >= 6) aResVals.push(String(v));
      else if (Array.isArray(v)) v.slice(0, 5).forEach((x): void => extractStrings(x, depth + 1));
      else if (v != null && typeof v === 'object')
        Object.values(v).forEach((x): void => extractStrings(x, depth + 1));
    }
    extractStrings(parsed);
  } catch {
    /* ignore */
  }

  // Also filter the B request string to strip infrastructure prefixes before matching
  // This prevents false positives on shared RPC method name fragments
  const bReqClean = bReqStr
    .replace(/"__auto_api\.[^"]*"/g, '""')
    .replace(/"_auto_api\.[^"]*"/g, '""');

  if (aResVals.length === 0) {
    return {
      dependent: false,
      confidence: 70,
      reason: 'Response contains no extractable identifiers',
    };
  }

  // Check if any A response value appears verbatim in B's request.
  // Also try to find the key name in A's response that produced the matched value.
  const matched = aResVals.find((v): boolean => bReqClean.includes(v));
  if (matched) {
    // Find which key in A's response this value came from
    let matchedKey: string | undefined;
    try {
      const parsed = typeof aRes === 'string' ? JSON.parse(aRes) : aRes;
      function findKey(obj: unknown, target: string, depth = 0): string | undefined {
        if (depth > 4 || obj == null) return undefined;
        if (typeof obj === 'object' && !Array.isArray(obj)) {
          for (const [k, v] of Object.entries(obj)) {
            if ((typeof v === 'string' || typeof v === 'number') && String(v) === target) return k;
            const sub = findKey(v, target, depth + 1);
            if (sub) return sub;
          }
        } else if (Array.isArray(obj)) {
          for (const item of obj.slice(0, 5)) {
            const sub = findKey(item, target, depth + 1);
            if (sub) return sub;
          }
        }
        return undefined;
      }
      matchedKey = findKey(parsed, matched);
    } catch {
      /* ignore */
    }
    return {
      dependent: true,
      confidence: 85,
      reason: 'Shared identifier detected between response and next request',
      matchedKey,
    };
  }

  return {
    dependent: false,
    confidence: 88,
    reason: 'No shared identifiers detected between calls',
  };
}

function analyzeExecution(events: ProcessedEvent[], executionTotalMs: number): ExecutionAnalysis {
  const ioSpans = events.filter(
    (e): boolean =>
      (e as { phase?: string }).phase === 'span' &&
      ['rpc', 'fetch', 'db'].includes(e.capability) &&
      (e.durationMs ?? 0) > 0,
  );

  // Top bottleneck span
  let topSpan: ProcessedEvent | null = null;
  for (const s of ioSpans) {
    if (!topSpan || (s.durationMs ?? 0) > (topSpan.durationMs ?? 0)) topSpan = s;
  }
  const topSpanPct =
    topSpan && executionTotalMs > 0
      ? Math.round(((topSpan.durationMs ?? 0) / executionTotalMs) * 100)
      : 0;

  // Sequential group detection: find clusters of IO spans that don't overlap
  // and could be parallelized. We look at spans sorted by start time and group
  // those that are mutually sequential (a greedy approach).
  const sorted = [...ioSpans].sort((a, b): number => a.at - b.at);
  const sequentialGroups: SequentialGroup[] = [];

  // Build groups: a group requires at least 2 spans, all pairs sequential
  // Use a sliding window — if all spans in the current group are sequential
  // with the next span, extend the group; otherwise flush and start fresh.
  let group: ProcessedEvent[] = [];
  for (const span of sorted) {
    if (group.length === 0) {
      group.push(span);
      continue;
    }
    // Check if this span is sequential with ALL spans already in the group
    // AND ensure there is no significant gap (meaning they are contiguous data-wise)
    const lastSpan = group[group.length - 1];
    const gapMs = span.at - (lastSpan!.at + (lastSpan!.durationMs ?? 0));
    const isAdjacent = gapMs >= -5 && gapMs <= 25; // allow up to 25ms gap

    const isSeqWithAll = group.every((g): boolean => spansAreSequential(g, span));
    if (isSeqWithAll && isAdjacent) {
      group.push(span);
    } else {
      if (group.length >= 2) {
        const totalMs = group.reduce((s, g): number => s + (g.durationMs ?? 0), 0);
        const parallelMs = Math.max(...group.map((g): number => g.durationMs ?? 0));
        const savingsMs = totalMs - parallelMs;
        if (savingsMs > 20) {
          // Check for inter-span dependencies (sequential pairs only)
          const sorted2 = [...group].sort((a, b): number => a.at - b.at);
          let dependent = false;
          let minConf = 100;
          let confReason = 'No shared identifiers detected between calls';
          let depHint: string | undefined;
          for (let i = 0; i < sorted2.length - 1; i++) {
            const dep = detectPayloadDependency(sorted2[i]!, sorted2[i + 1]!);
            if (dep.dependent) {
              dependent = true;
              confReason = dep.reason;
              if (!depHint && dep.matchedKey) {
                const aName = cleanSpanName(sorted2[i]!.fnName, sorted2[i]!.capability);
                const bName = cleanSpanName(sorted2[i + 1]!.fnName, sorted2[i + 1]!.capability);
                depHint = `"${dep.matchedKey}" from ${aName} used in ${bName} request`;
              }
            }
            if (dep.confidence < minConf) {
              minConf = dep.confidence;
              if (!dependent) confReason = dep.reason;
            }
          }
          sequentialGroups.push({
            spans: group,
            totalMs,
            parallelMs,
            savingsMs,
            savingsPct: Math.round((savingsMs / totalMs) * 100),
            likelyIndependent: !dependent,
            confidence: minConf,
            confidenceReason: confReason,
            ...(depHint != null ? { dependencyHint: depHint } : {}),
          });
        }
      }
      group = [span];
    }
  }
  if (group.length >= 2) {
    const totalMs = group.reduce((s, g): number => s + (g.durationMs ?? 0), 0);
    const parallelMs = Math.max(...group.map((g): number => g.durationMs ?? 0));
    const savingsMs = totalMs - parallelMs;
    if (savingsMs > 20) {
      const sorted2 = [...group].sort((a, b): number => a.at - b.at);
      let dependent = false;
      let minConf = 100;
      let confReason = 'No shared identifiers detected between calls';
      let depHint: string | undefined;
      for (let i = 0; i < sorted2.length - 1; i++) {
        const dep = detectPayloadDependency(sorted2[i]!, sorted2[i + 1]!);
        if (dep.dependent) {
          dependent = true;
          confReason = dep.reason;
          if (!depHint && dep.matchedKey) {
            const aName = cleanSpanName(sorted2[i]!.fnName, sorted2[i]!.capability);
            const bName = cleanSpanName(sorted2[i + 1]!.fnName, sorted2[i + 1]!.capability);
            depHint = `"${dep.matchedKey}" from ${aName} used in ${bName} request`;
          }
        }
        if (dep.confidence < minConf) {
          minConf = dep.confidence;
          if (!dependent) confReason = dep.reason;
        }
      }
      sequentialGroups.push({
        spans: group,
        totalMs,
        parallelMs,
        savingsMs,
        savingsPct: Math.round((savingsMs / totalMs) * 100),
        likelyIndependent: !dependent,
        confidence: minConf,
        confidenceReason: confReason,
        ...(depHint != null ? { dependencyHint: depHint } : {}),
      });
    }
  }

  // Impact simulation: how fast would this execution be if all sequential groups were parallelized?
  const totalSavingsMs = sequentialGroups
    .filter((g): boolean => g.likelyIndependent)
    .reduce((s, g): number => s + g.savingsMs, 0);
  const optimizedMs = Math.max(1, executionTotalMs - totalSavingsMs);
  const speedupFactor =
    optimizedMs > 0 ? Math.round((executionTotalMs / optimizedMs) * 10) / 10 : 1;

  return {
    topSpan,
    topSpanPct,
    executionTotalMs,
    sequentialGroups,
    optimizedMs,
    totalSavingsMs,
    speedupFactor,
  };
}

// ─── Execution-level insights card ─────────────────────────────────────────────

function ExecutionInsights({
  analysis,
  executionTotalMs: _executionTotalMs,
  onJumpToEvent,
  allEventIdx,
}: {
  analysis: ExecutionAnalysis;
  executionTotalMs: number;
  onJumpToEvent?: ((idx: number) => void) | undefined;
  allEventIdx: Map<ProcessedEvent, number>;
}): import('react/jsx-runtime').JSX.Element | null {
  const { topSpan, topSpanPct, sequentialGroups } = analysis;
  const [expanded, setExpanded] = useState(true);

  // Nothing interesting to show
  if (!topSpan && sequentialGroups.length === 0) return null;

  return (
    <div className="mx-3 mt-3 mb-1 rounded-lg border border-white/[0.07] bg-white/[0.025] overflow-hidden">
      {/* Header */}
      <button
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/[0.03] transition-colors"
        onClick={(): void => setExpanded((e): boolean => !e)}
      >
        <Lightbulb className="w-3 h-3 text-yellow-400/70 shrink-0" />
        <span className="flex-1 text-[11px] font-semibold text-foreground/70">
          Execution Insights
        </span>
        <ChevronRight
          className={`w-3 h-3 text-muted-foreground/30 transition-transform ${expanded ? 'rotate-90' : ''}`}
        />
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-white/[0.04]">
          {/* Top bottleneck */}
          {topSpan && topSpanPct >= 20 && (
            <div className="pt-2.5">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Target className="w-3 h-3 text-orange-400/70 shrink-0" />
                <span className="text-[10px] font-bold text-muted-foreground/50 uppercase tracking-wider">
                  Biggest bottleneck
                </span>
              </div>
              <div
                className="flex items-center gap-2 px-2 py-1.5 rounded bg-white/[0.03] hover:bg-white/[0.05] cursor-pointer"
                onClick={(): void => {
                  const idx = allEventIdx.get(topSpan) ?? -1;
                  if (idx >= 0) onJumpToEvent?.(idx);
                }}
              >
                <CapIcon cap={topSpan.capability} />
                <span className="flex-1 text-[11px] font-mono text-foreground/80 truncate">
                  {cleanSpanName(topSpan.fnName, topSpan.capability)}
                </span>
                <span className="text-[10px] font-mono font-bold text-orange-400/70">
                  {topSpan.durationMs}ms
                </span>
                <span className="text-[10px] text-muted-foreground/40">
                  ({topSpanPct}% of total)
                </span>
              </div>
              <p className="text-[10px] text-muted-foreground/40 mt-1 pl-1">
                Optimizing this span alone could reduce total latency by ~{topSpanPct}%.
              </p>
            </div>
          )}

          {/* Sequential call groups */}
          {sequentialGroups.map((group, gi): import('react/jsx-runtime').JSX.Element => {
            const optimizedGroupMs = group.parallelMs;
            const groupSpeedup =
              group.totalMs > 0 ? Math.round((group.totalMs / optimizedGroupMs) * 10) / 10 : 1;
            const confLabel =
              group.confidence >= 80 ? 'High' : group.confidence >= 60 ? 'Medium' : 'Low';
            // When dependency detected, high confidence = orange (confirms the warning)
            // When independent, high confidence = green (confirms safety)
            const confColor = group.likelyIndependent
              ? group.confidence >= 80
                ? 'text-emerald-400/70'
                : group.confidence >= 60
                  ? 'text-yellow-400/70'
                  : 'text-orange-400/70'
              : group.confidence >= 80
                ? 'text-orange-400/70'
                : group.confidence >= 60
                  ? 'text-yellow-400/70'
                  : 'text-muted-foreground/50';
            const spanNames = group.spans.map((s): string => cleanSpanName(s.fnName, s.capability));
            return (
              <div key={gi} className={topSpan ? 'pt-2 border-t border-white/[0.04]' : 'pt-2.5'}>
                {/* Section header */}
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Layers className="w-3 h-3 text-purple-400/60 shrink-0" />
                  <span className="text-[10px] font-bold text-muted-foreground/50 uppercase tracking-wider">
                    Sequential calls
                  </span>
                </div>

                {/* Span list */}
                <div className="space-y-0.5 mb-2">
                  {group.spans.map((s, si): import('react/jsx-runtime').JSX.Element => (
                    <div
                      key={si}
                      className="flex items-center gap-2 px-2 py-1 rounded hover:bg-white/[0.03] cursor-pointer"
                      onClick={(): void => {
                        const idx = allEventIdx.get(s) ?? -1;
                        if (idx >= 0) onJumpToEvent?.(idx);
                      }}
                    >
                      <CapIcon cap={s.capability} />
                      <span className="flex-1 text-[11px] font-mono text-foreground/70 truncate">
                        {cleanSpanName(s.fnName, s.capability)}
                      </span>
                      <span className="text-[10px] font-mono text-muted-foreground/50">
                        {s.durationMs}ms
                      </span>
                    </div>
                  ))}
                </div>

                {/* Layer 1: Clear yes/no decision */}
                <div
                  className={`flex items-center gap-2 px-2 py-1.5 rounded mb-1.5 ${
                    group.likelyIndependent
                      ? 'bg-emerald-500/[0.06] border border-emerald-500/[0.12]'
                      : 'bg-orange-500/[0.06] border border-orange-500/[0.12]'
                  }`}
                >
                  {group.likelyIndependent ? (
                    <CheckCircle2 className="w-3 h-3 text-emerald-400/80 shrink-0" />
                  ) : (
                    <AlertTriangle className="w-3 h-3 text-orange-400/80 shrink-0" />
                  )}
                  <span
                    className={`text-[10px] font-bold ${group.likelyIndependent ? 'text-emerald-400/90' : 'text-orange-400/90'}`}
                  >
                    Parallelization: {group.likelyIndependent ? 'SAFE' : 'NOT RECOMMENDED'}
                  </span>
                </div>

                {/* Layer 2: Reason + confidence */}
                <div className="px-1 space-y-0.5 mb-2">
                  <p className="text-[10px] text-muted-foreground/50">
                    Reason:{' '}
                    <span className="text-muted-foreground/70">
                      {group.likelyIndependent
                        ? 'No shared data between calls'
                        : 'Possible data dependency detected'}
                    </span>
                  </p>
                  <p className={`text-[10px] ${confColor}`}>
                    Confidence: {confLabel} ({group.confidence}%)
                  </p>
                  {/* Specific dependency hint when we found one */}
                  {!group.likelyIndependent && group.dependencyHint && (
                    <div className="mt-1 flex items-start gap-1.5 px-2 py-1.5 rounded bg-orange-500/5 border border-orange-500/10">
                      <Search className="w-2.5 h-2.5 text-orange-400/60 shrink-0 mt-0.5" />
                      <div className="text-[10px] text-muted-foreground/60">
                        <span className="text-orange-400/70 font-semibold">Dependency hint: </span>
                        {group.dependencyHint}
                      </div>
                    </div>
                  )}
                  {/* Or confirm independence */}
                  {group.likelyIndependent && (
                    <div className="mt-1 flex items-start gap-1.5 px-2 py-1.5 rounded bg-emerald-500/5 border border-emerald-500/10">
                      <Search className="w-2.5 h-2.5 text-emerald-400/60 shrink-0 mt-0.5" />
                      <p className="text-[10px] text-muted-foreground/60">
                        No shared inputs detected between calls
                      </p>
                    </div>
                  )}
                </div>

                {/* Savings + impact — only shown when safe */}
                {group.likelyIndependent && (
                  <div className="mt-1 px-1 space-y-1.5">
                    <p className="text-[10px] text-muted-foreground/50">
                      Running sequentially:{' '}
                      <span className="font-mono text-foreground/60">{group.totalMs}ms total</span>
                    </p>
                    <div className="px-2 py-1.5 rounded bg-purple-500/5 border border-purple-500/10">
                      <p className="text-[9px] font-bold text-purple-400/60 uppercase tracking-wider mb-1">
                        Estimated savings
                      </p>
                      <p className="text-[10px] font-mono">
                        <span className="text-muted-foreground/50">{group.totalMs}ms</span>
                        {' → '}
                        <span className="text-purple-400/80 font-bold">~{optimizedGroupMs}ms</span>
                        {groupSpeedup > 1 && (
                          <span className="ml-1.5 text-emerald-400/70 font-bold">
                            ~{groupSpeedup}x faster
                          </span>
                        )}
                        <span className="ml-1 text-[var(--flux-success)]/60">
                          {' '}
                          (saves {group.savingsMs}ms)
                        </span>
                      </p>
                    </div>
                    {/* Suggested Fix: Promise.all code block */}
                    <div className="rounded bg-white/[0.025] border border-white/[0.06] overflow-hidden">
                      <div className="flex items-center gap-1.5 px-2 py-1 border-b border-white/[0.06]">
                        <Zap className="w-2.5 h-2.5 text-yellow-400/60 shrink-0" />
                        <span className="text-[9px] font-bold text-muted-foreground/50 uppercase tracking-wider">
                          Suggested fix
                        </span>
                      </div>
                      <pre className="px-2 py-1.5 text-[10px] font-mono text-muted-foreground/60 overflow-x-auto leading-relaxed">{`await Promise.all([\n${spanNames.map((n): string => `  ${n}(),`).join('\n')}\n])`}</pre>
                    </div>
                  </div>
                )}

                {/* When NOT safe: still show the sequential cost */}
                {!group.likelyIndependent && (
                  <p className="text-[10px] text-muted-foreground/40 px-1">
                    Sequential cost:{' '}
                    <span className="font-mono text-foreground/50">{group.totalMs}ms</span>
                    {' — verify order dependency before refactoring.'}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface SpanFocus {
  span: ProcessedEvent;
  childSpans: ProcessedEvent[]; // spans whose window is inside this span
  logs: ProcessedEvent[];
  totalMs: number;
  externalMs: number; // time in child rpc/fetch/db spans (or totalMs for leaf network spans)
  computeMs: number; // totalMs - externalMs
  isLeafNetworkCall: boolean; // span itself IS the network call (no children, cap=rpc/fetch/db)
  status: number;
  isError: boolean;
  isSlow: boolean; // > 500ms
}

function buildSpanFocus(
  span: ProcessedEvent,
  allEvents: ProcessedEvent[],
  consoleLogs: ProcessedEvent[],
): SpanFocus {
  const spanStart = span.at;
  const spanEnd = span.at + (span.durationMs ?? 0);
  const totalMs = span.durationMs ?? 0;

  // Tolerance for floating-point/rounding edge: allow up to 5% of parent duration or 10ms, whichever is larger
  const tol = Math.max(10, totalMs * 0.05);

  // Child spans: any span phase, window fits inside the parent window
  const childSpans = allEvents.filter((e): boolean => {
    if (e === span) return false;
    if ((e as { phase?: string }).phase !== 'span') return false;
    const cap = e.capability;
    if (!['fetch', 'rpc', 'db'].includes(cap)) return false;
    return e.at >= spanStart && e.at + (e.durationMs ?? 0) <= spanEnd + tol;
  });

  // Logs inside this span's window
  const logs = consoleLogs.filter((l): boolean => l.at >= spanStart && l.at <= spanEnd);

  // If this span has no children but is itself a network/IO call, the
  // entire duration IS external wait — never classify it as compute.
  const isLeafNetworkCall =
    childSpans.length === 0 && ['rpc', 'fetch', 'db'].includes(span.capability);

  // External time = sum of child spans, or totalMs for a leaf network call
  const externalMs = isLeafNetworkCall
    ? totalMs
    : childSpans.reduce((s, c): number => s + (c.durationMs ?? 0), 0);
  const computeMs = Math.max(0, totalMs - externalMs);

  // Status from response payload
  const resRaw = (span as { responsePayload?: unknown }).responsePayload;

  const res: Record<string, unknown> | null =
    typeof resRaw === 'string'
      ? ((): Record<string, unknown> | null => {
          try {
            return JSON.parse(resRaw) as Record<string, unknown>;
          } catch {
            return null;
          }
        })()
      : (resRaw as Record<string, unknown> | null);
  const status = Number(res?.['status'] ?? 0);

  return {
    span,
    childSpans,
    logs,
    totalMs,
    externalMs,
    computeMs,
    isLeafNetworkCall,
    status,
    isError: status >= 500 || (span as { phase?: string }).phase === 'error',
    isSlow: totalMs > 500,
  };
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function isProtected(payload: unknown): boolean {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as Record<string, unknown>)['__protected'] === true
  );
}

function getLevelColor(
  level: string,
):
  | 'text-[var(--flux-warning)]'
  | 'text-[var(--flux-error)]'
  | 'text-muted-foreground/50'
  | 'text-foreground/80' {
  switch (level) {
    case 'error':
      return 'text-[var(--flux-error)]';
    case 'warn':
      return 'text-[var(--flux-warning)]';
    case 'debug':
      return 'text-muted-foreground/50';
    default:
      return 'text-foreground/80';
  }
}

function getLevelBg(level: string): '' | 'bg-[var(--flux-error)]/5' | 'bg-[var(--flux-warning)]/5' {
  switch (level) {
    case 'error':
      return 'bg-[var(--flux-error)]/5';
    case 'warn':
      return 'bg-[var(--flux-warning)]/5';
    default:
      return '';
  }
}

function fetchStatusColor(
  status: number,
):
  | 'text-[var(--flux-success)]'
  | 'text-[var(--flux-warning)]'
  | 'text-[var(--flux-error)]'
  | 'text-muted-foreground' {
  if (status >= 500) return 'text-[var(--flux-error)]';
  if (status >= 400) return 'text-[var(--flux-warning)]';
  if (status >= 200 && status < 300) return 'text-[var(--flux-success)]';
  return 'text-muted-foreground';
}

function formatPayload(payload: unknown): string {
  if (payload == null) return '';
  if (isProtected(payload)) return '[encrypted]';
  if (typeof payload === 'string') return payload;
  if (Array.isArray(payload)) return payload.map(formatPayload).join(' ');
  try {
    return JSON.stringify(payload);
  } catch {
    return String(payload as string | number | boolean | null | undefined);
  }
}

function capLabel(cap: string): string {
  if (cap === 'fetch') return 'Fetch';
  if (cap === 'rpc') return 'RPC';
  if (cap === 'db') return 'DB';
  return cap;
}

function CapIcon({ cap }: { cap: string }): import('react/jsx-runtime').JSX.Element {
  if (cap === 'fetch') return <Globe className="w-3 h-3 shrink-0 text-[var(--flux-fetch)]" />;
  if (cap === 'rpc') return <Zap className="w-3 h-3 shrink-0 text-purple-400/80" />;
  if (cap === 'db') return <Database className="w-3 h-3 shrink-0 text-blue-400/80" />;
  // "fn" = function wrapper with child calls
  if (cap === 'fn') return <ChevronRight className="w-3 h-3 shrink-0 text-foreground/30" />;
  return <ChevronRight className="w-3 h-3 shrink-0 text-muted-foreground/50" />;
}

function pct(part: number, total: number): number {
  if (!total) return 0;
  return Math.round((part / total) * 100);
}

// ─── Span-focused detail panel ─────────────────────────────────────────────────

function SpanDetail({
  focus,
  startAt,
  onBack,
  onJumpToEvent,
  allEventIdx,
}: {
  focus: SpanFocus;
  startAt: number;
  onBack: () => void;
  onJumpToEvent?: ((idx: number) => void) | undefined;
  allEventIdx: Map<ProcessedEvent, number>;
}): import('react/jsx-runtime').JSX.Element {
  const {
    span,
    childSpans,
    logs,
    totalMs,
    externalMs,
    computeMs,
    isLeafNetworkCall,
    status,
    isError,
    isSlow,
  } = focus;
  const offset = Math.round(span.at - startAt);
  const name = cleanSpanName(span.fnName, span.capability);
  const externalPct = pct(externalMs, totalMs);
  const cap = span.capability;

  // Span type label: wrapper function vs leaf call
  const isFunction = childSpans.length > 0;
  const rpcCount = childSpans.filter((c): boolean => c.capability === 'rpc').length;
  const fetchCount = childSpans.filter((c): boolean => c.capability === 'fetch').length;
  const dbCount = childSpans.filter((c): boolean => c.capability === 'db').length;
  const spanTypeLabel = isFunction ? 'Function' : capLabel(cap);

  // Correct classification: base on WHERE time is spent, not total duration.
  // A leaf rpc/fetch/db span IS the network call — externalMs = totalMs already.
  const isNetworkBound = externalPct >= 70; // includes leaf network calls
  const isCpuBound = !isNetworkBound && computeMs > 0 && computeMs >= totalMs * 0.7;

  // Dominant external call type (for wrapper functions)
  function dominantCallLabel(): string {
    if (isLeafNetworkCall) {
      if (cap === 'rpc') return 'RPC call';
      if (cap === 'fetch') return 'HTTP request';
      if (cap === 'db') return 'DB query';
    }
    // wrapper: find which child type dominates
    if (rpcCount >= fetchCount && rpcCount >= dbCount)
      return `${rpcCount} RPC call${rpcCount > 1 ? 's' : ''}`;
    if (fetchCount >= dbCount) return `${fetchCount} fetch call${fetchCount > 1 ? 's' : ''}`;
    return `${dbCount} DB quer${dbCount > 1 ? 'ies' : 'y'}`;
  }

  // Auto-insights (ordered by priority)
  interface Insight {
    icon: React.ReactNode;
    text: string;
    bullets?: string[];
    severity: 'warn' | 'info' | 'ok';
  }
  const insights: Insight[] = [];

  if (status >= 400) {
    insights.push({
      icon: <XCircle className="w-3 h-3 text-[var(--flux-error)]" />,
      text: `Returned error status ${status}. Check response payload.`,
      severity: 'warn',
    });
  }

  if (isNetworkBound) {
    if (isLeafNetworkCall) {
      // This span itself is the network call — no children to blame
      const callKind = cap === 'rpc' ? 'RPC' : cap === 'fetch' ? 'HTTP' : 'DB';
      insights.push({
        icon: <Globe className="w-3 h-3 text-purple-400/60" />,
        text: `${callKind} call: ${totalMs}ms — 100% network/IO wait`,
        bullets: [
          'Bottleneck is external service latency',
          'Add caching if this result is repeatable',
          'Batch with other calls using Promise.all',
          'Reduce payload size to decrease transfer time',
        ],
        severity: isSlow ? 'warn' : 'info',
      });
    } else {
      insights.push({
        icon: <Globe className="w-3 h-3 text-purple-400/60" />,
        text: `Network-bound: ${externalPct}% of time in external calls (${externalMs}ms of ${totalMs}ms)`,
        bullets: [
          `Compute: ~${computeMs}ms — External: ~${externalMs}ms`,
          `Dominated by ${dominantCallLabel()}`,
          'Parallelize independent calls with Promise.all',
          'Cache or batch repeated calls',
        ],
        severity: 'warn',
      });
    }
  } else if (isCpuBound) {
    insights.push({
      icon: <Clock className="w-3 h-3 text-[var(--flux-warning)]" />,
      text: `${pct(computeMs, totalMs)}% compute time (${computeMs}ms) — this function is CPU-bound.`,
      bullets: [
        'Check for blocking operations or heavy loops',
        'Consider moving work to a background task',
      ],
      severity: 'warn',
    });
  } else if (!isError && !isSlow) {
    insights.push({
      icon: <CheckCircle2 className="w-3 h-3 text-[var(--flux-success)]" />,
      text: `Completed in ${totalMs}ms.`,
      severity: 'ok',
    });
  } else if (isSlow && !isNetworkBound && !isCpuBound) {
    insights.push({
      icon: <Clock className="w-3 h-3 text-[var(--flux-warning)]" />,
      text: `Slow (${totalMs}ms) but mixed compute and IO. Profile individual steps.`,
      severity: 'warn',
    });
  }

  // Sequential child span detection (only for wrapper functions with ≥2 children)
  const childSeqGroup: SequentialGroup | null = ((): SequentialGroup | null => {
    if (childSpans.length < 2) return null;
    const sorted = [...childSpans].sort((a, b): number => a.at - b.at);
    // Ensure sequence and contiguous execution (no gaps >25ms map)
    const allSeq = sorted.every((s, i): boolean => {
      if (i === 0) return true;
      const prev = sorted[i - 1];
      const gapMs = s.at - (prev!.at + (prev!.durationMs ?? 0));
      const isAdjacent = gapMs >= -5 && gapMs <= 25;
      return spansAreSequential(prev!, s) && isAdjacent;
    });
    if (!allSeq) return null;
    const seqTotalMs = sorted.reduce((s, c): number => s + (c.durationMs ?? 0), 0);
    const parallelMs = Math.max(...sorted.map((c): number => c.durationMs ?? 0));
    const savingsMs = seqTotalMs - parallelMs;
    if (savingsMs < 20) return null;
    // Run dependency heuristic on child spans
    let dependent = false;
    let minConf = 100;
    let confReason = 'No shared identifiers detected between calls';
    let depHint: string | undefined;
    for (let i = 0; i < sorted.length - 1; i++) {
      const dep = detectPayloadDependency(sorted[i]!, sorted[i + 1]!);
      if (dep.dependent) {
        dependent = true;
        confReason = dep.reason;
        if (!depHint && dep.matchedKey) {
          const aName = cleanSpanName(sorted[i]!.fnName, sorted[i]!.capability);
          const bName = cleanSpanName(sorted[i + 1]!.fnName, sorted[i + 1]!.capability);
          depHint = `"${dep.matchedKey}" from ${aName} used in ${bName} request`;
        }
      }
      if (dep.confidence < minConf) {
        minConf = dep.confidence;
        if (!dependent) confReason = dep.reason;
      }
    }
    return {
      spans: sorted,
      totalMs: seqTotalMs,
      parallelMs,
      savingsMs,
      savingsPct: Math.round((savingsMs / seqTotalMs) * 100),
      likelyIndependent: !dependent,
      confidence: minConf,
      confidenceReason: confReason,
      dependencyHint: depHint,
    };
  })();

  return (
    <div className="flex flex-col gap-0 h-full overflow-auto">
      {/* Header */}
      <div className="px-4 pt-3 pb-2 border-b border-white/5 shrink-0">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-[10px] text-muted-foreground/40 hover:text-muted-foreground transition-colors mb-2"
        >
          <ArrowLeft className="w-3 h-3" /> All events
        </button>
        <div className="flex items-start gap-2">
          <CapIcon cap={isFunction ? 'fn' : cap} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-foreground font-mono">{name}</span>
              <span className="text-[10px] text-muted-foreground/40 uppercase font-bold tracking-wider">
                {spanTypeLabel}
              </span>
              {status > 0 && (
                <span className={`text-[10px] font-bold font-mono ${fetchStatusColor(status)}`}>
                  {status}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <span
                className={`text-[11px] font-mono font-bold ${isError ? 'text-[var(--flux-error)]' : isSlow ? 'text-[var(--flux-warning)]' : 'text-[var(--flux-success)]'}`}
              >
                {totalMs}ms
              </span>
              {isSlow && (
                <span className="flex items-center gap-1 text-[10px] text-[var(--flux-warning)]">
                  <AlertTriangle className="w-2.5 h-2.5" /> Slow
                </span>
              )}
              {isNetworkBound && (
                <span className="text-[10px] text-purple-400/60 font-medium">network-bound</span>
              )}
              {isCpuBound && (
                <span className="text-[10px] text-blue-400/60 font-medium">cpu-bound</span>
              )}
              {isFunction && childSpans.length > 0 && (
                <span className="text-[10px] text-muted-foreground/30 font-mono">
                  {[
                    rpcCount > 0 && `${rpcCount} RPC`,
                    fetchCount > 0 && `${fetchCount} fetch`,
                    dbCount > 0 && `${dbCount} DB`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              )}
              <span className="text-[10px] text-muted-foreground/40 font-mono">+{offset}ms</span>
            </div>
          </div>
        </div>
      </div>

      {/* Timing breakdown — show for wrapper spans (child calls) or leaf network calls */}
      {totalMs > 0 && (childSpans.length > 0 || isLeafNetworkCall) && (
        <div className="px-4 py-3 border-b border-white/5 shrink-0">
          <p className="text-[10px] font-semibold text-muted-foreground/40 uppercase tracking-wider mb-2">
            Time breakdown
          </p>
          <div className="space-y-1.5">
            {/* Leaf network call: the entire span is network/IO wait */}
            {isLeafNetworkCall ? (
              <div className="flex items-center gap-2">
                <div className="w-24 text-[10px] text-muted-foreground/50 font-mono capitalize">
                  {cap === 'rpc' ? 'RPC wait' : cap === 'fetch' ? 'HTTP wait' : 'DB wait'}
                </div>
                <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-purple-400/60 rounded-full" style={{ width: '100%' }} />
                </div>
                <div className="w-20 text-right text-[10px] font-mono text-muted-foreground/60">
                  {totalMs}ms <span className="text-muted-foreground/30">(100%)</span>
                </div>
              </div>
            ) : (
              <>
                {externalMs > 0 && (
                  <div className="flex items-center gap-2">
                    <div className="w-24 text-[10px] text-muted-foreground/50 font-mono">
                      External
                    </div>
                    <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-purple-400/50 rounded-full"
                        style={{ width: `${externalPct}%` }}
                      />
                    </div>
                    <div className="w-20 text-right text-[10px] font-mono text-muted-foreground/60">
                      {externalMs}ms{' '}
                      <span className="text-muted-foreground/30">({externalPct}%)</span>
                    </div>
                  </div>
                )}
                {computeMs > 0 && (
                  <div className="flex items-center gap-2">
                    <div className="w-24 text-[10px] text-muted-foreground/50 font-mono">
                      Compute
                    </div>
                    <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-400/50 rounded-full"
                        style={{ width: `${pct(computeMs, totalMs)}%` }}
                      />
                    </div>
                    <div className="w-20 text-right text-[10px] font-mono text-muted-foreground/60">
                      {computeMs}ms{' '}
                      <span className="text-muted-foreground/30">({pct(computeMs, totalMs)}%)</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Child spans */}
      {childSpans.length > 0 && (
        <div className="px-4 py-3 border-b border-white/5 shrink-0">
          <p className="text-[10px] font-semibold text-muted-foreground/40 uppercase tracking-wider mb-2">
            Child calls
          </p>
          <div className="space-y-1">
            {childSpans.map((child, ci): import('react/jsx-runtime').JSX.Element => {
              const childName = cleanSpanName(child.fnName, child.capability);
              const childDur = child.durationMs ?? 0;
              const childOffset = Math.round(child.at - startAt);
              const childIdx = allEventIdx.get(child) ?? -1;
              const childResRaw = (child as { responsePayload?: unknown }).responsePayload;

              const childRes: Record<string, unknown> | null =
                typeof childResRaw === 'string'
                  ? ((): Record<string, unknown> | null => {
                      try {
                        return JSON.parse(childResRaw) as Record<string, unknown>;
                      } catch {
                        return null;
                      }
                    })()
                  : (childResRaw as Record<string, unknown> | null);
              const childStatus = Number(childRes?.['status'] ?? 0);
              return (
                <div
                  key={`child-${child.id}-${ci}`}
                  className="flex items-center py-1 px-2 rounded hover:bg-white/[0.03] cursor-pointer group"
                  onClick={(): void | undefined => onJumpToEvent?.(childIdx)}
                >
                  <div className="w-4 shrink-0 flex items-center justify-center">
                    <CapIcon cap={child.capability} />
                  </div>
                  <span className="flex-1 text-[11px] font-mono text-foreground/70 truncate pl-2">
                    {childName}
                  </span>
                  <div className="w-8 shrink-0 text-right pr-2">
                    {childStatus > 0 && (
                      <span
                        className={`text-[10px] font-mono font-bold ${fetchStatusColor(childStatus)}`}
                      >
                        {childStatus}
                      </span>
                    )}
                  </div>
                  <div className="w-12 shrink-0 text-right">
                    <span
                      className={`text-[10px] font-mono tabular-nums ${childDur > 500 ? 'text-[var(--flux-warning)]' : 'text-muted-foreground/40'}`}
                    >
                      {childDur}ms
                    </span>
                  </div>
                  <div className="w-14 shrink-0 text-right">
                    <span className="text-[10px] font-mono text-muted-foreground/40">
                      +{childOffset}ms
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Sequential calls warning — inside this wrapper function */}
      {childSeqGroup &&
        ((): import('react/jsx-runtime').JSX.Element => {
          const optimizedGroupMs = childSeqGroup.parallelMs;
          const groupSpeedup =
            childSeqGroup.totalMs > 0
              ? Math.round((childSeqGroup.totalMs / optimizedGroupMs) * 10) / 10
              : 1;
          const confLabel =
            childSeqGroup.confidence >= 80
              ? 'High'
              : childSeqGroup.confidence >= 60
                ? 'Medium'
                : 'Low';
          const confColor = childSeqGroup.likelyIndependent
            ? childSeqGroup.confidence >= 80
              ? 'text-emerald-400/70'
              : childSeqGroup.confidence >= 60
                ? 'text-yellow-400/70'
                : 'text-orange-400/70'
            : childSeqGroup.confidence >= 80
              ? 'text-orange-400/70'
              : childSeqGroup.confidence >= 60
                ? 'text-yellow-400/70'
                : 'text-muted-foreground/50';
          const childSpanNames = childSeqGroup.spans.map((s): string =>
            cleanSpanName(s.fnName, s.capability),
          );
          return (
            <div className="px-4 py-3 border-b border-white/5 shrink-0">
              {/* Header */}
              <div className="flex items-center gap-1.5 mb-2">
                <Layers className="w-3 h-3 text-purple-400/60 shrink-0" />
                <p className="text-[10px] font-semibold text-muted-foreground/40 uppercase tracking-wider">
                  Sequential calls
                </p>
              </div>

              {/* Layer 1: Decision */}
              <div
                className={`flex items-center gap-2 px-2 py-1.5 rounded mb-2 ${
                  childSeqGroup.likelyIndependent
                    ? 'bg-emerald-500/[0.06] border border-emerald-500/[0.12]'
                    : 'bg-orange-500/[0.06] border border-orange-500/[0.12]'
                }`}
              >
                {childSeqGroup.likelyIndependent ? (
                  <CheckCircle2 className="w-3 h-3 text-emerald-400/80 shrink-0" />
                ) : (
                  <AlertTriangle className="w-3 h-3 text-orange-400/80 shrink-0" />
                )}
                <span
                  className={`text-[10px] font-bold ${childSeqGroup.likelyIndependent ? 'text-emerald-400/90' : 'text-orange-400/90'}`}
                >
                  Parallelization: {childSeqGroup.likelyIndependent ? 'SAFE' : 'NOT RECOMMENDED'}
                </span>
              </div>

              {/* Layer 2: Reason + confidence */}
              <div className="space-y-0.5 mb-2 px-1">
                <p className="text-[10px] text-muted-foreground/50">
                  Reason:{' '}
                  <span className="text-muted-foreground/70">
                    {childSeqGroup.likelyIndependent
                      ? 'No shared data between calls'
                      : 'Possible data dependency detected'}
                  </span>
                </p>
                <p className={`text-[10px] ${confColor}`}>
                  Confidence: {confLabel} ({childSeqGroup.confidence}%)
                </p>
                {!childSeqGroup.likelyIndependent && childSeqGroup.dependencyHint && (
                  <div className="mt-1 flex items-start gap-1.5 px-2 py-1.5 rounded bg-orange-500/5 border border-orange-500/10">
                    <Search className="w-2.5 h-2.5 text-orange-400/60 shrink-0 mt-0.5" />
                    <p className="text-[10px] text-muted-foreground/60">
                      <span className="text-orange-400/70 font-semibold">Dependency hint: </span>
                      {childSeqGroup.dependencyHint}
                    </p>
                  </div>
                )}
                {childSeqGroup.likelyIndependent && (
                  <div className="mt-1 flex items-start gap-1.5 px-2 py-1.5 rounded bg-emerald-500/5 border border-emerald-500/10">
                    <Search className="w-2.5 h-2.5 text-emerald-400/60 shrink-0 mt-0.5" />
                    <p className="text-[10px] text-muted-foreground/60">
                      No shared inputs detected between calls
                    </p>
                  </div>
                )}
              </div>

              {/* Savings + fix preview */}
              {childSeqGroup.likelyIndependent && (
                <div className="space-y-1.5 px-1">
                  <div className="px-2 py-1.5 rounded bg-purple-500/5 border border-purple-500/10">
                    <p className="text-[9px] font-bold text-purple-400/60 uppercase tracking-wider mb-1">
                      Estimated savings
                    </p>
                    <p className="text-[10px] font-mono">
                      <span className="text-muted-foreground/50">{childSeqGroup.totalMs}ms</span>
                      {' → '}
                      <span className="text-purple-400/80 font-bold">~{optimizedGroupMs}ms</span>
                      {groupSpeedup > 1 && (
                        <span className="ml-1.5 text-emerald-400/70 font-bold">
                          ~{groupSpeedup}x faster
                        </span>
                      )}
                      <span className="ml-1 text-[var(--flux-success)]/60">
                        {' '}
                        (saves {childSeqGroup.savingsMs}ms)
                      </span>
                    </p>
                  </div>
                  <div className="rounded bg-white/[0.025] border border-white/[0.06] overflow-hidden">
                    <div className="flex items-center gap-1.5 px-2 py-1 border-b border-white/[0.06]">
                      <Zap className="w-2.5 h-2.5 text-yellow-400/60 shrink-0" />
                      <span className="text-[9px] font-bold text-muted-foreground/50 uppercase tracking-wider">
                        Suggested fix
                      </span>
                    </div>
                    <pre className="px-2 py-1.5 text-[10px] font-mono text-muted-foreground/60 overflow-x-auto leading-relaxed">{`await Promise.all([\n${childSpanNames.map((n): string => `  ${n}(),`).join('\n')}\n])`}</pre>
                  </div>
                </div>
              )}
              {!childSeqGroup.likelyIndependent && (
                <p className="text-[10px] text-muted-foreground/40 px-1">
                  Sequential cost:{' '}
                  <span className="font-mono text-foreground/50">{childSeqGroup.totalMs}ms</span>
                  {' — verify order dependency before refactoring.'}
                </p>
              )}
            </div>
          );
        })()}

      {/* Console logs inside span */}
      {logs.length > 0 && (
        <div className="px-4 py-3 border-b border-white/5 shrink-0">
          <p className="text-[10px] font-semibold text-muted-foreground/40 uppercase tracking-wider mb-2">
            Logs
          </p>
          <div className="space-y-0.5">
            {logs.map((log, li): import('react/jsx-runtime').JSX.Element => {
              const logOffset = Math.round(log.at - startAt);
              const logIdx = allEventIdx.get(log) ?? -1;
              return (
                <div
                  key={`slog-${log.id}-${li}`}
                  className={`flex items-start gap-2 py-1 px-2 rounded cursor-pointer hover:bg-white/[0.03] text-[11px] font-mono ${getLevelBg(log.action)}`}
                  onClick={(): void | undefined => onJumpToEvent?.(logIdx)}
                >
                  <span
                    className={`w-10 shrink-0 font-bold text-[10px] uppercase ${getLevelColor(log.action)}`}
                  >
                    {log.action}
                  </span>
                  <span className={`flex-1 min-w-0 leading-relaxed ${getLevelColor(log.action)}`}>
                    <RenderPayload payload={log.payload} className={getLevelColor(log.action)} />
                  </span>
                  <span className="text-muted-foreground/40 tabular-nums shrink-0">
                    +{logOffset}ms
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Insights */}
      {insights.length > 0 && (
        <div className="px-4 py-3 shrink-0">
          <p className="text-[10px] font-semibold text-muted-foreground/40 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Lightbulb className="w-3 h-3" /> Insights
          </p>
          <div className="space-y-3">
            {insights.map((ins, ii): import('react/jsx-runtime').JSX.Element => (
              <div key={ii} className="flex items-start gap-2">
                <div className="mt-0.5 shrink-0">{ins.icon}</div>
                <div>
                  <p
                    className={`text-[11px] leading-snug ${ins.severity === 'ok' ? 'text-muted-foreground/60' : 'text-foreground/80'}`}
                  >
                    {ins.text}
                  </p>
                  {ins.bullets && ins.bullets.length > 0 && (
                    <ul className="mt-1.5 space-y-0.5">
                      {ins.bullets.map((b, bi): import('react/jsx-runtime').JSX.Element => (
                        <li
                          key={bi}
                          className="flex items-start gap-1.5 text-[10px] text-muted-foreground/50"
                        >
                          <span className="mt-0.5 text-muted-foreground/30">→</span>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Render helpers shared between modes ───────────────────────────────────────

function RenderArg({
  value,
  className,
}: {
  value: unknown;
  className?: string | undefined;
}): import('react/jsx-runtime').JSX.Element {
  if (typeof value === 'string') {
    const t = value.trim();
    if ((t.startsWith('{') || t.startsWith('[')) && t.length > 2) {
      try {
        // eslint-disable-next-line react-hooks/error-boundaries
        return <JsonHighlight value={JSON.parse(t)} inline />;
      } catch {
        /* fall */
      }
    }
    return <span className={className}>{value}</span>;
  }
  if (value !== null && typeof value === 'object') return <JsonHighlight value={value} inline />;
  return <span className={className}>{(value as string | undefined) ?? ''}</span>;
}

function RenderPayload({
  payload,
  className,
}: {
  payload: unknown;
  className?: string | undefined;
}): import('react/jsx-runtime').JSX.Element {
  if (isProtected(payload)) {
    return <span className="italic text-muted-foreground/50">[encrypted payload]</span>;
  }
  if (Array.isArray(payload)) {
    return (
      <span className="inline-flex gap-1.5 flex-wrap items-baseline">
        {payload.map((arg, i): import('react/jsx-runtime').JSX.Element => (
          <RenderArg key={i} value={arg} className={className} />
        ))}
      </span>
    );
  }
  return <SmartMessage text={formatPayload(payload)} className={className} />;
}

function LogRow({
  event,
  startAt,
  isSelected,
  onClick,
  indent = false,
}: {
  event: ProcessedEvent;
  startAt: number;
  isSelected: boolean;
  onClick: () => void;
  indent?: boolean;
}): import('react/jsx-runtime').JSX.Element {
  const offset = Math.round(event.at - startAt);
  const level = event.action;
  return (
    <div
      id={`console-row-${event.seq}`}
      className={`flex items-start gap-2 py-1.5 cursor-pointer transition-colors text-xs font-mono
        ${isSelected ? 'bg-blue-500/10 outline outline-1 outline-blue-500/30' : `hover:bg-white/[0.03] ${getLevelBg(level)}`}
        ${indent ? 'pl-8 border-l-2 border-white/[0.06] ml-6' : 'px-4'}
      `}
      onClick={onClick}
    >
      <span className="text-muted-foreground/40 tabular-nums w-10 shrink-0 text-right select-none">
        {event.seq}
      </span>
      <span
        className={`w-12 text-[10px] font-bold uppercase tracking-wider shrink-0 ${getLevelColor(level)}`}
      >
        {level}
      </span>
      <span className={`flex-1 min-w-0 leading-relaxed ${getLevelColor(level)}`}>
        <RenderPayload payload={event.payload} className={getLevelColor(level)} />
      </span>
      <span className="text-muted-foreground/40 tabular-nums shrink-0 text-right w-14">
        +{offset}ms
      </span>
    </div>
  );
}

function SpanRow({
  event,
  logs,
  startAt,
  logFilter,
  search,
  onLogClick,
  onSpanClick,
  selectedIdx,
  allEventIdx,
}: {
  event: ProcessedEvent;
  logs: ProcessedEvent[];
  startAt: number;
  logFilter: LogLevel;
  search: string;
  onLogClick: (idx: number) => void;
  onSpanClick: (idx: number) => void;
  selectedIdx: number | null | undefined;
  allEventIdx: Map<ProcessedEvent, number>;
}): import('react/jsx-runtime').JSX.Element {
  const [collapsed, setCollapsed] = useState(false);
  const offset = Math.round(event.at - startAt);
  const cap = event.capability;
  const label = cleanSpanName(event.fnName, cap);

  let statusBadge: React.ReactNode = null;
  if (cap === 'fetch') {
    const resPayload = (event as { responsePayload?: unknown }).responsePayload;

    const res =
      typeof resPayload === 'string'
        ? ((): Record<string, unknown> | null => {
            try {
              return JSON.parse(resPayload) as Record<string, unknown>;
            } catch {
              return null;
            }
          })()
        : (resPayload as Record<string, unknown> | null);
    const status = Number(res?.['status'] ?? 0);
    if (status)
      statusBadge = (
        <span className={`${fetchStatusColor(status)} font-bold text-[10px]`}>{status}</span>
      );
  }

  const visibleLogs = logs.filter((log): boolean => {
    if (logFilter !== 'all' && log.action !== logFilter) return false;
    if (search && !formatPayload(log.payload).toLowerCase().includes(search.toLowerCase()))
      return false;
    return true;
  });

  const hasLogs = visibleLogs.length > 0;
  const spanIdx = allEventIdx.get(event) ?? -1;
  const isSelected = selectedIdx === spanIdx;
  const dur = event.durationMs ?? 0;

  return (
    <div>
      <div
        id={`console-row-${event.seq}`}
        className={`flex items-center gap-2 px-4 py-1.5 cursor-pointer select-none group transition-colors
          ${isSelected ? 'bg-blue-500/10 outline outline-1 outline-blue-500/30' : 'hover:bg-white/[0.025]'}`}
        onClick={(): void => {
          onSpanClick(spanIdx);
          if (hasLogs) setCollapsed((c): boolean => !c);
        }}
      >
        <span className="text-muted-foreground/40 tabular-nums w-10 shrink-0 text-right text-xs font-mono select-none" />
        <CapIcon cap={cap} />
        <span className="flex-1 text-xs font-mono font-medium text-foreground/70 truncate">
          {label}
        </span>
        <div className="w-8 shrink-0 text-right">{statusBadge}</div>
        <div className="w-12 shrink-0 text-right">
          {dur > 0 && (
            <span
              className={`text-[10px] font-mono tabular-nums ${dur > 500 ? 'text-[var(--flux-warning)]' : 'text-muted-foreground/40'}`}
            >
              {dur}ms
            </span>
          )}
        </div>
        <span className="text-muted-foreground/40 tabular-nums text-xs font-mono shrink-0 w-14 text-right">
          +{offset}ms
        </span>
        {hasLogs && (
          <ChevronRight
            className={`w-3 h-3 shrink-0 text-muted-foreground/30 transition-transform ${collapsed ? '' : 'rotate-90'}`}
          />
        )}
      </div>
      {hasLogs && !collapsed && (
        <div>
          {visibleLogs.map((log): import('react/jsx-runtime').JSX.Element => {
            const evIdx = allEventIdx.get(log) ?? -1;
            return (
              <LogRow
                key={`${log.id}-${log.at}`}
                event={log}
                startAt={startAt}
                isSelected={selectedIdx === evIdx}
                onClick={(): void => onLogClick(evIdx)}
                indent
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

export function ConsolePanel({
  onJumpToEvent,
  selectedIdx,
}: ConsolePanelProps): import('react/jsx-runtime').JSX.Element {
  const { events, consoleLogs, startAt } = useExecutionData();
  const [filter, setFilter] = useState<LogLevel>('all');
  const [search, setSearch] = useState('');
  // When true, user has manually dismissed focus mode (pinned to global view),
  // or it starts true by default so initial load shows the summary.
  const [pinnedGlobal, setPinnedGlobal] = useState(true);
  const [prevSelectedIdx, setPrevSelectedIdx] = useState(selectedIdx);

  // If selectedIdx changes from outside (e.g. user clicked timeline),
  // we drop the pinnedGlobal toggle so it enters focus mode instantly.
  if (selectedIdx !== prevSelectedIdx) {
    setPrevSelectedIdx(selectedIdx);
    setPinnedGlobal(false);
  }

  const scrollRef = useRef<HTMLDivElement>(null);

  const allEventIdx = React.useMemo<Map<ProcessedEvent, number>>((): Map<
    ProcessedEvent,
    number
  > => {
    const m = new Map<ProcessedEvent, number>();
    events.forEach((e, i): Map<ProcessedEvent, number> => m.set(e, i));
    return m;
  }, [events]);

  const narrative = React.useMemo(
    (): NarrativeItem[] => buildNarrative(events, consoleLogs),
    [events, consoleLogs],
  );

  // Execution-level analysis (bottleneck + sequential groups)
  const { durationMs: executionTotalMs } = useExecutionData();
  const executionAnalysis = React.useMemo(
    (): ExecutionAnalysis => analyzeExecution(events, executionTotalMs),
    [events, executionTotalMs],
  );

  // Determine if selected event is a span we can drill into (any cap that is a span)
  const selectedEvent =
    selectedIdx !== null && selectedIdx !== undefined ? events[selectedIdx] : null;
  const selectedIsSpan =
    selectedEvent !== null && (selectedEvent as { phase?: string }).phase === 'span';

  // Note: we removed the useEffect for syncing pinnedGlobal down here in favor of the
  // inline render-phase state update above.

  const handleJumpToEvent = (idx: number): void => {
    setPinnedGlobal(false); // Force details open even if the clicked index is already the "selected" one
    onJumpToEvent?.(idx);
  };

  const showFocus = selectedIsSpan && !pinnedGlobal;

  const spanFocus = React.useMemo<SpanFocus | null>((): SpanFocus | null => {
    if (!showFocus || selectedEvent == null) return null;
    return buildSpanFocus(selectedEvent, events, consoleLogs);
  }, [showFocus, selectedEvent, events, consoleLogs]);

  // Scroll to selected row in global view
  useEffect((): void => {
    if (showFocus || selectedIdx === null || selectedIdx === undefined) return;
    const ev = events[selectedIdx];
    if (ev == null) return;
    const el = document.getElementById(`console-row-${ev.seq}`);
    if (el && scrollRef.current) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [showFocus, selectedIdx, events]);

  const hasConsoleLogs = consoleLogs.length > 0;
  const levelCounts = React.useMemo((): Record<string, number> => {
    const counts: Record<string, number> = {};
    for (const log of consoleLogs) counts[log.action] = (counts[log.action] ?? 0) + 1;
    return counts;
  }, [consoleLogs]);

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar — hide in focus mode */}
      {!showFocus && (
        <div className="flex items-center gap-2 px-4 py-2 border-b shrink-0 bg-[#0a0c11]">
          <div className="flex gap-0.5">
            {LOG_LEVELS.map((level): import('react/jsx-runtime').JSX.Element => {
              const count = level === 'all' ? consoleLogs.length : (levelCounts[level] ?? 0);
              return (
                <button
                  key={level}
                  onClick={(): void => setFilter(level)}
                  className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors flex items-center gap-1 ${
                    filter === level
                      ? 'bg-accent text-accent-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {level.charAt(0).toUpperCase() + level.slice(1)}
                  {count > 0 && level !== 'all' && (
                    <span
                      className={`text-[9px] tabular-nums ${level === 'error' ? 'text-[var(--flux-error)]' : level === 'warn' ? 'text-[var(--flux-warning)]' : 'text-muted-foreground/50'}`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="relative flex-1 max-w-xs ml-auto">
            <Search className="absolute left-2 top-1.5 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search messages..."
              className="h-7 pl-7 text-xs bg-black/20 focus-visible:bg-[#0a0c11] border-white/10"
              value={search}
              onChange={(e): void => setSearch(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-auto font-mono text-xs bg-[#0a0c11]" ref={scrollRef}>
        {/* ── Span focus mode ── */}
        {showFocus && spanFocus ? (
          <SpanDetail
            focus={spanFocus}
            startAt={startAt}
            onBack={(): void => setPinnedGlobal(true)}
            onJumpToEvent={handleJumpToEvent}
            allEventIdx={allEventIdx}
          />
        ) : narrative.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 px-4 py-14 text-center">
            <Terminal className="h-8 w-8 text-muted-foreground/30" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">No execution data</p>
              <p className="text-xs text-muted-foreground/60">
                Events will appear here once the execution runs.
              </p>
            </div>
          </div>
        ) : (
          /* ── Global narrative mode ── */
          <div className="py-1">
            {/* Execution-level insights card (bottleneck + sequential detection) */}
            {!search && filter === 'all' && (
              <ExecutionInsights
                analysis={executionAnalysis}
                executionTotalMs={executionTotalMs}
                onJumpToEvent={handleJumpToEvent}
                allEventIdx={allEventIdx}
              />
            )}
            {narrative.map((item, i): import('react/jsx-runtime').JSX.Element | null => {
              if (item.kind === 'milestone') {
                const offset = Math.round(item.event.at - startAt);
                const { milestoneKind, label } = item;
                return (
                  <div
                    key={`m-${i}`}
                    className="flex items-center gap-3 px-4 py-2 text-[10px] font-mono text-muted-foreground/40 select-none"
                  >
                    <div className="flex-1 h-px bg-white/5" />
                    {milestoneKind === 'start' && (
                      <Play className="w-3 h-3 text-[var(--flux-success)]/60" />
                    )}
                    {milestoneKind === 'end' && (
                      <CheckCircle2 className="w-3 h-3 text-[var(--flux-success)]/60" />
                    )}
                    {milestoneKind === 'error' && (
                      <XCircle className="w-3 h-3 text-[var(--flux-error)]/60" />
                    )}
                    <span
                      className={milestoneKind === 'error' ? 'text-[var(--flux-error)]/60' : ''}
                    >
                      {label}
                    </span>
                    <span className="text-muted-foreground/40">+{offset}ms</span>
                    <div className="flex-1 h-px bg-white/5" />
                  </div>
                );
              }

              if (item.kind === 'span') {
                const spanMatchesSearch =
                  !search ||
                  cleanSpanName(item.event.fnName, item.event.capability)
                    .toLowerCase()
                    .includes(search.toLowerCase());
                const hasMatchingLogs = item.logs.some((log): boolean => {
                  if (filter !== 'all' && log.action !== filter) return false;
                  if (
                    search &&
                    !formatPayload(log.payload).toLowerCase().includes(search.toLowerCase())
                  )
                    return false;
                  return true;
                });
                if (search && !spanMatchesSearch && !hasMatchingLogs) return null;
                return (
                  <SpanRow
                    key={`span-${item.event.id}-${item.event.at}`}
                    event={item.event}
                    logs={item.logs}
                    startAt={startAt}
                    logFilter={filter}
                    search={search}
                    onLogClick={(idx): void => handleJumpToEvent(idx)}
                    onSpanClick={(idx): void => handleJumpToEvent(idx)}
                    selectedIdx={selectedIdx}
                    allEventIdx={allEventIdx}
                  />
                );
              }

              if (item.kind === 'log') {
                if (filter !== 'all' && item.event.action !== filter) return null;
                if (
                  search &&
                  !formatPayload(item.event.payload).toLowerCase().includes(search.toLowerCase())
                )
                  return null;
                const evIdx = allEventIdx.get(item.event) ?? -1;
                return (
                  <LogRow
                    key={`log-${item.event.id}-${item.event.at}`}
                    event={item.event}
                    startAt={startAt}
                    isSelected={selectedIdx === evIdx}
                    onClick={(): void => handleJumpToEvent(evIdx)}
                  />
                );
              }

              return null;
            })}

            {hasConsoleLogs &&
              filter !== 'all' &&
              ((): import('react/jsx-runtime').JSX.Element | null => {
                const anyVisible = narrative.some((item): boolean => {
                  if (item.kind === 'log') return item.event.action === filter;
                  if (item.kind === 'span')
                    return item.logs.some((l): boolean => l.action === filter);
                  return false;
                });
                return !anyVisible ? (
                  <div className="flex items-center justify-center py-10 text-muted-foreground/40 text-xs">
                    No {filter} messages in this execution
                  </div>
                ) : null;
              })()}

            {!hasConsoleLogs && narrative.some((n): boolean => n.kind === 'span') && (
              <div className="px-4 py-3 text-[10px] text-muted-foreground/50 font-mono italic text-center">
                No <code className="bg-white/5 px-1 rounded">console.warn()</code> calls — the
                timeline above shows execution structure
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
